import { useCallback, useMemo } from "react";
import dayjs from "dayjs";

export const useBookingValidation = (
  formData,
  enforceAvailability,
  tutors,
  isTutorAvailable
) => {
  const isFutureTimeSlot = useCallback(({ date, start, end }) => {
    if (!date || !start || !end) return false;
    const now = dayjs();
    const startTime = dayjs(`${date} ${start}`);
    const endTime = dayjs(`${date} ${end}`);
    return startTime.isAfter(now) && endTime.isAfter(startTime);
  }, []);

  const getTimeSlotError = useCallback(({ date, start, end }) => {
    if (!date || !start || !end) return null;
    const now = dayjs();
    const startTime = dayjs(`${date} ${start}`);
    const endTime = dayjs(`${date} ${end}`);
    if (startTime.isBefore(now)) return "Cannot book in the past";
    if (endTime.isBefore(startTime)) return "End time must be after start time";
    return null;
  }, []);

  const validateStep1 = useCallback(() => {
    const errors = {};
    if (!formData.subjectId) errors.subject = "Please select a subject";
    formData.preferredTimes.forEach((time, index) => {
      const timeError = getTimeSlotError(time);
      if (timeError) errors[`timeSlot${index}`] = timeError;
    });
    return { isValid: Object.keys(errors).length === 0, errors };
  }, [formData, getTimeSlotError]);

  const canProceedToStep2 = useMemo(() => {
    return (
      formData.subjectId && formData.preferredTimes.every(isFutureTimeSlot)
    );
  }, [formData, isFutureTimeSlot]);

  const availableTutors = useMemo(() => {
    if (!enforceAvailability) return tutors;
    return tutors.filter((tutor) =>
      formData.preferredTimes.some((time) => isTutorAvailable(tutor, time))
    );
  }, [tutors, formData.preferredTimes, enforceAvailability, isTutorAvailable]);

  // Removed the incorrect loading reference
  const canSubmit = useMemo(() => {
    // This will be passed from the parent component
    return formData.useRandom || formData.selectedTutorIds.length > 0;
  }, [formData.useRandom, formData.selectedTutorIds]);

  return {
    isFutureTimeSlot,
    getTimeSlotError,
    validateStep1,
    canProceedToStep2,
    availableTutors,
    canSubmit,
  };
};
